<template>
  <!--
    Owner signup — single-page stepper wizard.

      Step 1 — Account Information
        Name, brokerage, cell, email, brokerage address.
        SMS + Email consent.

      Step 2 — Agreement
        Market summary, pricing breakdown, optional coupon, optional
        Free Tier Partner Agreement (when coupon contains "FREE"),
        scroll-gated Billing Terms + Terms of Service, acceptance
        checkbox. Single clickwrap covers ToS + Billing Terms.

      Step 3 — Billing
        Final price recap, Stripe Payment Element, Subscribe button.

    Market territory is chosen upstream on /my-market and lands here as
    URL query params; the persistent price strip in the header surfaces
    it across all three steps. If the user lands here without a market,
    a prompt sends them back to draw one before step 1 unlocks.
  -->
  <div class="signup-page">
    <div class="signup-card" :aria-busy="submitting ? 'true' : 'false'">

      <header class="signup-header">
        <div class="signup-header__row">
          <div class="signup-header__title-block">
            <h1 class="signup-title">Real Estate Team Sign Up</h1>
            <p class="signup-subtitle">
              Already have an account?
              <router-link to="/login" class="signup-link">Login</router-link>
            </p>
          </div>
          <div v-if="zip_count" class="signup-header__price-block">
            <div class="price-strip" :class="{ 'price-strip--free': is_free_tier || is_trial_promo }">
              <div class="price-strip__amount" v-if="is_free_tier">
                Free<span class="price-strip__per">/mo</span>
              </div>
              <div class="price-strip__amount" v-else-if="is_trial_promo">
                Free<span class="price-strip__per">/mo · {{ coupon_details.duration_in_months }}mo</span>
              </div>
              <div class="price-strip__amount" v-else-if="coupon_applied && coupon_details">
                ${{ formatted_discounted_price }}<span class="price-strip__per">/mo</span>
              </div>
              <div class="price-strip__amount" v-else>
                ${{ formatted_price }}<span class="price-strip__per">/mo</span>
              </div>
              <div class="price-strip__note">
                {{ zip_count }} ZIP{{ zip_count === 1 ? '' : 's' }}
                <span v-if="market_population">· {{ market_population.toLocaleString('en-US') }} pop</span>
                <span v-if="!is_free_tier">· + tax</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div v-if="existingOwnerTenant" class="existing-owner-banner">
        <div class="existing-owner-banner__body">
          <strong>You already own
            <span v-if="existingOwnerTenant.company_name">"{{ existingOwnerTenant.company_name }}"</span>
            <span v-else>a business</span>
            on Kiey.</strong>
          Continue below to add another business, or go to your dashboard.
        </div>
        <div class="existing-owner-banner__actions">
          <button type="button" class="existing-owner-banner__btn-primary" @click="goToDashboard">Go to dashboard</button>
          <button type="button" class="existing-owner-banner__btn-ghost" @click="dismissExistingOwner">Add another</button>
        </div>
      </div>

      <!-- ============== Stepper indicator ============== -->
      <nav class="stepper" aria-label="Sign-up progress">
        <ol class="stepper__list">
          <li
            v-for="step in steps"
            :key="step.n"
            class="stepper__item"
            :class="stepperItemClasses(step.n)"
          >
            <button
              type="button"
              class="stepper__btn"
              :disabled="!canJumpTo(step.n)"
              :aria-current="current_step === step.n ? 'step' : null"
              @click="jumpTo(step.n)"
            >
              <span class="stepper__bullet">
                <span v-if="isStepCompleted(step.n)" class="stepper__check" aria-hidden="true">✓</span>
                <span v-else>{{ step.n }}</span>
              </span>
              <span class="stepper__label">{{ step.label }}</span>
            </button>
            <span v-if="step.n < steps.length" class="stepper__connector" aria-hidden="true"></span>
          </li>
        </ol>
        <div class="stepper__bar">
          <div class="stepper__bar-fill" :style="{ width: progressPct + '%' }"></div>
        </div>
        <div class="stepper__mobile">Step {{ current_step }} of {{ steps.length }} · {{ currentStepLabel }}</div>
      </nav>

      <ValidationObserver ref="validator" v-slot="{}">

        <!-- ============== Step 1 — Account Information ============== -->
        <section v-show="current_step === 1" class="step-pane" aria-labelledby="step-1-title">
          <h2 id="step-1-title" class="step-title">Account Information</h2>
          <p class="step-caption">Tell us who you are and how to reach you.</p>

          <v-form>
            <div class="account-grid">
              <div class="account-grid__cell">
                <ValidationProvider mode="lazy" name="firstName" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="first_name"
                    :error-messages="errors"
                    class="ui-label change_bord especial_border support-input sing_up"
                    label="First Name"
                    name="firstName"
                    type="text"
                  ></v-text-field>
                </ValidationProvider>
              </div>
              <div class="account-grid__cell">
                <ValidationProvider mode="lazy" name="lastName" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="last_name"
                    :error-messages="errors"
                    class="ui-label change_bord especial_border support-input sing_up"
                    label="Last Name"
                    name="lastName"
                    type="text"
                  ></v-text-field>
                </ValidationProvider>
              </div>
              <div class="account-grid__cell">
                <ValidationProvider mode="lazy" name="companyName" rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model="company_name"
                    :error-messages="errors"
                    class="ui-label change_bord especial_border support-input sing_up"
                    label="Brokerage Name"
                    name="companyName"
                    type="text"
                  ></v-text-field>
                </ValidationProvider>
              </div>
              <div class="account-grid__cell">
                <ValidationProvider ref="phone_number" mode="lazy" name="phone_number" rules="required|size:10" v-slot="{ errors }">
                  <v-text-field
                    v-model="phone_number"
                    :error-messages="errors"
                    v-mask="mask"
                    class="ui-label change_bord especial_border support-input sing_up"
                    label="Username (Cell Phone Number)"
                    name="phone_number"
                    type="tel"
                    inputmode="tel"
                  ></v-text-field>
                </ValidationProvider>
              </div>
              <div class="account-grid__cell">
                <ValidationProvider mode="lazy" name="email" rules="required|email" v-slot="{ errors }">
                  <v-text-field
                    v-model="email"
                    :error-messages="errors"
                    class="ui-label change_bord especial_border support-input sing_up"
                    label="Email"
                    name="email"
                    type="text"
                  ></v-text-field>
                </ValidationProvider>
              </div>
              <div class="account-grid__cell">
                <ValidationProvider ref="company_address" mode="lazy" name="company_address" rules="required" v-slot="{ errors }">
                  <v-text-field
                    ref="addressField"
                    v-model="company_address"
                    :error-messages="errors"
                    class="ui-label change_bord especial_border support-input sing_up"
                    label="Brokerage Address"
                    name="company_address"
                    type="text"
                  ></v-text-field>
                </ValidationProvider>
              </div>
            </div>
            <div class="signup-consent-tight">
              <consent-checkboxes
                :sms-consent.sync="sms_consent"
                :email-consent.sync="email_consent"
              />
            </div>
          </v-form>
        </section>

        <!-- ============== Step 2 — Agreement ============== -->
        <section v-show="current_step === 2" class="step-pane" aria-labelledby="step-2-title">
          <h2 id="step-2-title" class="step-title">Agreement</h2>
          <p class="step-caption">Confirm what you're being charged and accept the terms.</p>

          <h3 class="step-subheading">Pricing Breakdown</h3>
          <p class="section-caption">
            Pricing scales with your market &mdash; $1 per 1,000 residents, with a $295 monthly minimum.
          </p>
          <div class="pricing-card">
            <div class="pricing-row">
              <div class="pricing-label">Market</div>
              <div class="pricing-value">
                <span v-if="market_label">{{ market_label }}</span>
                <span v-else class="muted">No market selected</span>
              </div>
            </div>
            <div class="pricing-row">
              <div class="pricing-label">Population</div>
              <div class="pricing-value">
                <span v-if="market_population">{{ market_population.toLocaleString('en-US') }}</span>
                <span v-else class="muted">&mdash;</span>
              </div>
            </div>
            <div class="pricing-row pricing-row-emphasis">
              <div class="pricing-label">Your monthly fee</div>
              <div class="pricing-value" v-if="is_free_tier">
                <span class="pricing-strikethrough">${{ formatted_price }}.00</span>
                <span class="pricing-free-badge">Free</span>
              </div>
              <div class="pricing-value" v-else-if="is_trial_promo">
                <span class="pricing-free-badge">Free</span>
                <span class="pricing-trial-after">for {{ coupon_details.duration_in_months }} mo, then ${{ formatted_price }}.00</span>
              </div>
              <div class="pricing-value" v-else-if="coupon_applied && coupon_details">
                <span class="pricing-strikethrough">${{ formatted_price }}.00</span>
                ${{ formatted_discounted_price }}
              </div>
              <div class="pricing-value" v-else>${{ formatted_price }}.00</div>
            </div>
            <div class="pricing-row pricing-row-tax">
              <div class="pricing-label">Sales tax</div>
              <div class="pricing-value pricing-tax-note">Calculated automatically by Stripe</div>
            </div>
            <div class="pricing-divider"></div>
            <div class="pricing-row pricing-row-total">
              <div class="pricing-label">Estimated total today</div>
              <div class="pricing-value" v-if="is_free_tier">$0.00 / month</div>
              <div class="pricing-value" v-else-if="is_trial_promo">$0.00 for {{ coupon_details.duration_in_months }} months</div>
              <div class="pricing-value" v-else-if="coupon_applied && coupon_details">
                ${{ formatted_discounted_price }} + tax / month
              </div>
              <div class="pricing-value" v-else>${{ formatted_price }}.00 + tax / month</div>
            </div>
            <div v-if="is_free_tier" class="pricing-coupon-note">
              Coupon <code>{{ coupon_applied_code }}</code> applied — <strong>100% off forever</strong>. No payment method needed.
            </div>
            <div v-else-if="is_trial_promo" class="pricing-coupon-note">
              Coupon <code>{{ coupon_applied_code }}</code> applied — <strong>free for {{ coupon_details.duration_in_months }} month{{ coupon_details.duration_in_months === 1 ? '' : 's' }}</strong>, then ${{ formatted_price }}.00/mo. Card on file, no charge until the promo ends.
            </div>
            <div v-else-if="coupon_applied && coupon_details" class="pricing-coupon-note">
              Coupon <code>{{ coupon_applied_code }}</code> applied — <strong>{{ coupon_discount_label }}</strong><span v-if="coupon_details.duration === 'forever'"> forever</span><span v-else-if="coupon_details.duration === 'once'"> · first invoice only, then back to ${{ formatted_price }}.00/mo</span>.
            </div>
          </div>

          <div class="coupon-section">
            <button
              v-if="!coupon_open"
              type="button"
              class="coupon-toggle"
              @click="coupon_open = true"
            >
              Have a coupon code?
            </button>
            <div v-else class="coupon-panel">
              <label class="coupon-label" for="coupon-code-input-step2">Coupon code</label>
              <div class="coupon-row">
                <input
                  id="coupon-code-input-step2"
                  type="text"
                  class="coupon-input"
                  v-model.trim="coupon_input"
                  :disabled="coupon_applied"
                  placeholder="e.g. WELCOME50"
                  autocomplete="off"
                  spellcheck="false"
                  @keyup.enter="applyCoupon"
                />
                <button
                  v-if="!coupon_applied"
                  type="button"
                  class="coupon-btn"
                  :disabled="!coupon_input || coupon_validating"
                  @click="applyCoupon"
                >
                  <span v-if="coupon_validating">Checking…</span>
                  <span v-else>Apply</span>
                </button>
                <button
                  v-else
                  type="button"
                  class="coupon-btn coupon-btn-clear"
                  @click="clearCoupon"
                >
                  Remove
                </button>
              </div>
              <p v-if="coupon_error" class="coupon-note coupon-note-error">
                {{ coupon_error }}
              </p>
              <p v-else-if="coupon_applied" class="coupon-note coupon-note-ok">
                Applied — <strong>{{ coupon_discount_label }}</strong>
                <span v-if="coupon_details && coupon_details.duration === 'forever'"> forever</span>
                <span v-else-if="coupon_details && coupon_details.duration === 'once'"> · first invoice only</span>
                <span v-else-if="coupon_details && coupon_details.duration === 'repeating' && coupon_details.duration_in_months">
                  · first {{ coupon_details.duration_in_months }} months
                </span>.
              </p>
              <p v-else class="coupon-note">
                Enter your code and click Apply to validate.
              </p>
            </div>
          </div>

          <div v-if="show_free_tier_terms" class="free-tier-block">
            <h3 class="step-subheading">Free Tier Partner Agreement</h3>
            <div class="terms-scrollbox terms-scrollbox-full" aria-label="Free Tier Partner Agreement">
              <div v-if="free_tier_terms_loading" class="muted">Loading the Free Tier Partner Agreement&hellip;</div>
              <div v-else-if="free_tier_terms_error" class="muted">{{ free_tier_terms_error }}</div>
              <article v-else class="free-terms-body" v-html="free_tier_terms_html"></article>
            </div>
            <label class="terms-consent">
              <input type="checkbox" v-model="free_tier_terms_accepted" />
              <span>
                I have read and agree to the Kiey Free Tier Partner Agreement
                and the spirit of the partnership it describes.
              </span>
            </label>
          </div>

          <h3 class="step-subheading step-subheading-spaced">Billing Terms &amp; Terms of Service</h3>
          <p class="terms-instruction" :class="{ 'terms-instruction-done': terms_scrolled }">
            <span v-if="!terms_scrolled">
              <strong>Please scroll to the bottom</strong> of the box below before you can accept.
            </span>
            <span v-else>
              <strong>✓ You've reached the end.</strong> You can now accept the terms below.
            </span>
          </p>
          <div
            ref="terms_scrollbox"
            class="terms-scrollbox"
            tabindex="0"
            aria-label="Kiey Billing Terms — scroll to read"
            @scroll="onTermsScroll"
          >
            <h3 class="terms-heading">Kiey Billing Terms <span class="terms-version">v{{ billing_terms_version }}</span></h3>
            <ul class="terms-list">
              <li>
                <strong>Auto-renewal.</strong> Your subscription renews automatically on the same day each month
                until you cancel. The payment method you enter on the next step is charged the monthly amount
                displayed above (plus applicable tax) on a recurring basis.
              </li>
              <li>
                <strong>Cancellation.</strong> You can cancel anytime through your account settings.
                A 30-day notice period applies. During the notice period your subscription stays
                active and your market territory is preserved.
              </li>
              <li>
                <strong>Prorated refund.</strong> Once the 30-day notice period ends, Kiey issues a prorated
                refund for any unused days within the final billing cycle.
              </li>
              <li>
                <strong>Pricing changes.</strong> Kiey may change subscription pricing with at least 30 days&rsquo;
                advance notice. Population recalculation within the existing per-1,000 formula does
                not require separate notice — your market price tracks your population at each
                renewal.
              </li>
              <li>
                <strong>Failed payments.</strong> If a recurring charge fails, Kiey provides a 7-day grace
                period and up to 3 automatic retry attempts. After the grace period, the subscription
                is suspended and your market territory returns to the public waitlist.
              </li>
              <li>
                <strong>Tax.</strong> Applicable U.S. state and local sales tax and Canadian GST/HST/PST/QST are
                calculated automatically at checkout and on every renewal invoice based on your
                billing address.
              </li>
              <li>
                <strong>Receipts.</strong> Receipts are available in your account at admin.kiey.com under
                Account &rarr; Billing, and are also emailed by Stripe after every successful charge.
              </li>
              <li>
                <strong>Payment processor.</strong> All charges are processed by Stripe. Card data is
                entered into Stripe's hosted card field on the next step — Kiey never sees or stores
                your full card number.
              </li>
              <li>
                <strong>Currency.</strong> Charges are billed in U.S. Dollars (USD), except where Kiey expressly
                invoices a Canadian customer in Canadian Dollars (CAD).
              </li>
              <li>
                <strong>Real Estate Team responsibilities.</strong> You are responsible for vetting the Vendors
                and Agents you invite to your market, and for ensuring their conduct meets your
                professional standards. See Section 12A of the Terms of Service for the full duty
                of care.
              </li>
            </ul>
            <p class="terms-footer">
              These billing terms are part of, and subject to, the full Kiey Terms of Service below.
              In case of conflict, the Terms of Service control.
            </p>
            <hr class="terms-divider" />
            <h3 class="terms-heading">Kiey Terms of Service <span class="terms-version">v{{ billing_terms_version }}</span></h3>
            <div v-if="full_tos_loading" class="muted">Loading the full Terms of Service&hellip;</div>
            <div v-else-if="full_tos_error" class="muted">{{ full_tos_error }}</div>
            <div v-else class="terms-fulltos" v-html="full_tos_html"></div>
            <div ref="terms_end_marker" class="terms-end-marker">
              <span aria-hidden="true">— End of Terms —</span>
            </div>
          </div>
          <p v-if="!terms_scrolled" class="terms-scroll-hint">
            Please scroll to the end of the Terms above to enable acceptance.
          </p>
          <label class="terms-consent" :class="{ 'terms-consent-disabled': !terms_scrolled }">
            <input
              type="checkbox"
              v-model="billing_terms_accepted"
              :disabled="!terms_scrolled"
              :aria-disabled="!terms_scrolled ? 'true' : 'false'"
            />
            <span>
              I have read and agree to the Kiey Billing Terms and Terms of Service above,
              and authorize recurring monthly charges to the payment method I provide on the next step.
            </span>
          </label>
        </section>

        <!-- ============== Step 3 — Billing ============== -->
        <section v-show="current_step === 3" class="step-pane" aria-labelledby="step-3-title">
          <h2 id="step-3-title" class="step-title">Billing</h2>
          <p class="step-caption">Add your payment method to activate your subscription.</p>

          <!-- Read-only coupon summary. Coupon entry + Free Tier Partner
               Agreement live on Step 2 (Agreement) where they belong;
               Step 3 just reflects what was applied. Change link sends
               the user back to Step 2 to edit or remove. -->
          <div v-if="coupon_applied && coupon_details" class="coupon-summary-step3">
            <div class="coupon-summary-row">
              <span class="coupon-summary-label">Coupon</span>
              <span class="coupon-summary-value">
                <strong>{{ coupon_applied_code }}</strong>
                <span class="coupon-summary-detail">
                  · {{ coupon_discount_label }}<span v-if="coupon_details.duration === 'forever'"> forever</span><span v-else-if="coupon_details.duration === 'once'"> · first invoice only</span><span v-else-if="coupon_details.duration === 'repeating' && coupon_details.duration_in_months"> · first {{ coupon_details.duration_in_months }} months</span>
                </span>
              </span>
              <button type="button" class="coupon-summary-change" @click="current_step = 2">
                Change
              </button>
            </div>
          </div>

          <div class="pricing-card pricing-card-recap">
            <div class="pricing-row pricing-row-emphasis">
              <div class="pricing-label">Today's authorization</div>
              <div class="pricing-value" v-if="is_free_tier">$0.00 — Free</div>
              <div class="pricing-value" v-else-if="is_trial_promo">
                $0.00 today · ${{ formatted_price }}.00/mo after
              </div>
              <div class="pricing-value" v-else-if="coupon_applied && coupon_details">
                ${{ formatted_discounted_price }} + tax / month
              </div>
              <div class="pricing-value" v-else>${{ formatted_price }}.00 + tax / month</div>
            </div>
            <p class="pricing-recap-note">
              <span v-if="is_free_tier">
                Free tier — no charge today. No payment method needed; if you ever transition to paid pricing, you'll be prompted to add one then.
              </span>
              <span v-else-if="is_trial_promo">
                Free for the first {{ coupon_details.duration_in_months }} month{{ coupon_details.duration_in_months === 1 ? '' : 's' }}. Your card is on file but not charged until the promo ends — then <strong>${{ formatted_price }}.00 + tax</strong> on the same day each month.
              </span>
              <span v-else-if="coupon_applied && coupon_details">
                Your card is authorized today and charged
                <strong>${{ formatted_discounted_price }} + tax</strong>
                on the same day each month
                <span v-if="coupon_details.duration === 'once'"> for the first invoice, then ${{ formatted_price }}.00/mo</span>
                <span v-else-if="coupon_details.duration === 'repeating' && coupon_details.duration_in_months">
                  for the first {{ coupon_details.duration_in_months }} months, then ${{ formatted_price }}.00/mo
                </span>.
              </span>
              <span v-else>
                Your card is authorized today and charged on the same day each month going forward.
              </span>
            </p>
          </div>

          <!-- Payment Method section: hidden entirely for free-tier
               signups (no charge, so no card needed). Paid + discounted
               coupon paths still need a card. -->
          <template v-if="!is_free_tier">
            <h3 class="step-subheading step-subheading-spaced">Payment Method</h3>
            <p class="section-caption">
              Card data is entered directly into Stripe — Kiey never sees or stores your full card number.
              Apple Pay, Google Pay, and Link appear automatically when supported.
            </p>
            <div v-if="!stripe_publishable_key" class="payment-warning">
              Stripe is not configured for this environment. Set <code>VUE_APP_STRIPE_PUBLISHABLE_KEY</code> in
              the environment file and rebuild.
            </div>
            <div v-else>
              <div ref="stripe_payment_element" class="stripe-element-mount"></div>
              <p v-if="stripe_load_error" class="payment-warning">{{ stripe_load_error }}</p>
              <p v-else-if="!stripe_elements_ready" class="payment-loading muted">Loading secure payment form&hellip;</p>
            </div>
          </template>
        </section>

        <!-- ============== Footer nav (Back / Continue / Subscribe) ============== -->
        <div class="step-nav">
          <button
            v-if="current_step > 1"
            type="button"
            class="step-nav__back"
            :disabled="submitting"
            @click="goBack"
          >
            ← Back
          </button>
          <span v-else></span>

          <div class="step-nav__forward">
            <button
              v-if="current_step < 3"
              type="button"
              class="step-nav__continue"
              :disabled="!currentStepValid"
              @click="goNext"
            >
              Continue →
            </button>
            <button
              v-else
              type="button"
              class="step-nav__subscribe"
              :disabled="!can_submit"
              @click="onSubscribeClick"
            >
              <span v-if="submitting">{{ submit_label_busy }}</span>
              <span v-else>{{ submit_label }}</span>
            </button>
          </div>
        </div>

        <p v-if="error_message" class="subscribe-error">{{ error_message }}</p>
        <p v-else-if="current_step === 3" class="subscribe-footer">
          Powered by Stripe. Your payment information is processed securely by Stripe.
        </p>

      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { mask } from "vue-the-mask";
import { mapState, mapGetters } from "vuex";
import config from "@/config/constants.js";
import ConsentCheckboxes from "@/components/forms/ConsentCheckboxes";
import { extractLocation } from "@/util/places";
import { renderLegalMarkdown } from "@/util/legal_md.js";
import { floored_market_price, MARKET_PRICE_FLOOR } from "@/util/market_pricing";
import { loadStripe } from "@stripe/stripe-js";

const BILLING_TERMS_VERSION = '2026-05-03';

export default {
  name: "SignUpOwner",
  directives: { mask },
  components: { ValidationProvider, ValidationObserver, ConsentCheckboxes },

  data() {
    return {
      mask: "### - ### - ####",
      current_step: 1,
      max_step_reached: 1,
      steps: [
        { n: 1, label: 'Account' },
        { n: 2, label: 'Agreement' },
        { n: 3, label: 'Billing' },
      ],
      // Account fields
      first_name: "",
      last_name: "",
      company_name: "",
      phone_number: "",
      email: "",
      company_address: "",
      company_location: null,
      sms_consent: false,
      email_consent: false,
      // Polygon claimed on /my-market
      claimed_zips: [],
      claimed_pricing_mode: 'calculated',
      // Coupon
      coupon_open: false,
      coupon_input: '',
      coupon_applied_code: '',
      // Populated after a successful POST /v1/coupons/validate. Carries
      // { id, name, valid, percent_off, amount_off, duration } so the UI
      // can render the actual discounted monthly fee instead of a vague
      // "may differ" note.
      coupon_details: null,
      coupon_validating: false,
      coupon_error: '',
      // Billing terms gate
      billing_terms_accepted: false,
      billing_terms_version: BILLING_TERMS_VERSION,
      terms_scrolled: false,
      // Free Tier Partner Agreement (conditional)
      free_tier_terms_html: '',
      free_tier_terms_loading: false,
      free_tier_terms_error: '',
      free_tier_terms_loaded: false,
      free_tier_terms_accepted: false,
      free_terms_version: config.free_terms_current_version,
      // Full ToS lazy-load
      full_tos_html: '',
      full_tos_loading: false,
      full_tos_error: '',
      full_tos_loaded: false,
      // Stripe state
      stripe_publishable_key: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || '',
      stripe: null,
      stripe_elements: null,
      stripe_payment_element: null,
      stripe_setup_intent_id: null,
      stripe_setup_intent_client_secret: null,
      stripe_elements_ready: false,
      stripe_load_error: '',
      stripe_initialized: false,
      // Submit state
      submitting: false,
      error_message: '',
      // Existing-owner banner
      existingOwnerCheckDone: false,
      existingOwnerTenant: null,
    };
  },

  computed: {
    ...mapState({
      status: (state) => state.Owner_sign_up.status,
      user_sign_up: (state) => state.Owner_sign_up.user,
      authenticatedUser: (state) => (state.Authenticate && state.Authenticate.user) || null,
      my_market: (state) => state.Market_billing && state.Market_billing.my_market,
      billing_info: (state) => state.Market_billing && state.Market_billing.billing_info,
    }),
    ...mapGetters('Market_billing', ['zip_population_map']),
    zip_count() {
      if (Array.isArray(this.claimed_zips) && this.claimed_zips.length > 0) {
        return this.claimed_zips.length;
      }
      if (this.my_market && Array.isArray(this.my_market.zip_codes)) {
        return this.my_market.zip_codes.length;
      }
      return 0;
    },
    market_label() {
      if (!this.zip_count) return '';
      return `${this.zip_count} ZIP${this.zip_count === 1 ? '' : 's'} claimed`;
    },
    market_population() {
      if (this.billing_info && typeof this.billing_info.monthly_price_calculated === 'number'
          && this.billing_info.monthly_price_calculated > 0) {
        return this.billing_info.monthly_price_calculated * 1000;
      }
      // Sum real ZIP populations from the Vuex zip_population_map. Falls
      // back to a 7,500-per-ZIP estimate only if the map isn't loaded yet
      // (load_zip_data is dispatched in mounted but is async).
      const zips = (this.claimed_zips && this.claimed_zips.length > 0)
        ? this.claimed_zips
        : (this.my_market && Array.isArray(this.my_market.zip_codes) ? this.my_market.zip_codes : []);
      if (zips.length === 0) return 0;
      const map = this.zip_population_map || {};
      let total = 0;
      let unknown = 0;
      for (const zip of zips) {
        if (map[zip] != null) total += map[zip];
        else unknown++;
      }
      if (total === 0 && unknown === zips.length) return zips.length * 7500;
      return total;
    },
    effective_price() {
      // Unlike the market map, this screen quotes the floor as a starting
      // price when no market has been sized yet.
      return floored_market_price(this.market_population) || MARKET_PRICE_FLOOR;
    },
    formatted_price() {
      return Math.round(this.effective_price).toLocaleString('en-US');
    },
    // Discounted price after applying the validated coupon. When the
    // backend coupon-validate endpoint has returned percent_off or
    // amount_off, compute the real discounted monthly fee. When the
    // coupon isn't validated yet (or there's none), this equals
    // effective_price so the UI shows the un-discounted total.
    discounted_price() {
      const base = this.effective_price;
      const d = this.coupon_details;
      if (!d || !d.valid) return base;
      if (typeof d.percent_off === 'number' && d.percent_off > 0) {
        return Math.max(0, base * (1 - d.percent_off / 100));
      }
      if (typeof d.amount_off === 'number' && d.amount_off > 0) {
        // Stripe amount_off is in cents.
        return Math.max(0, base - (d.amount_off / 100));
      }
      return base;
    },
    formatted_discounted_price() {
      return this.discounted_price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    coupon_discount_label() {
      const d = this.coupon_details;
      if (!d || !d.valid) return '';
      if (typeof d.percent_off === 'number' && d.percent_off > 0) {
        // Drop trailing .0 — "50% off" not "50.0% off"
        const pct = d.percent_off % 1 === 0 ? d.percent_off : d.percent_off.toFixed(1);
        return `${pct}% off`;
      }
      if (typeof d.amount_off === 'number' && d.amount_off > 0) {
        return `$${(d.amount_off / 100).toFixed(2)} off`;
      }
      return '';
    },
    coupon_applied() {
      return !!this.coupon_applied_code;
    },
    show_free_tier_terms() {
      // Free Tier Partner Agreement only applies to *permanently* free
      // accounts. Time-limited 100%-off coupons (e.g. "60 days free
      // then paid") aren't the partner-tier — they're a paid subscription
      // with a promotional intro period.
      if (!this.coupon_applied) return false;
      const d = this.coupon_details;
      if (!d || !d.valid) return false;
      return d.percent_off === 100 && d.duration === 'forever';
    },
    is_free_tier() {
      // "Truly free" — no card collection, no future billing. Used to
      // hide the Payment Method section on step 3 and skip the
      // SetupIntent confirmation in onSubscribeClick.
      return this.show_free_tier_terms;
    },
    is_trial_promo() {
      // 100% off for N months — card still needed because billing
      // resumes at full price after the promo period.
      if (!this.coupon_applied) return false;
      const d = this.coupon_details;
      if (!d || !d.valid) return false;
      return d.percent_off === 100
        && d.duration === 'repeating'
        && d.duration_in_months > 0;
    },

    // ---- Per-step validity ------------------------------------------
    step_1_valid() {
      return !!(this.first_name && this.last_name && this.company_name
        && this.phone_number && this.phone_number.replace(/\D/g, '').length >= 10
        && this.email && /\S+@\S+\.\S+/.test(this.email)
        && this.company_address
        && this.sms_consent && this.email_consent);
    },
    step_2_valid() {
      if (!this.terms_scrolled) return false;
      if (!this.billing_terms_accepted) return false;
      if (this.show_free_tier_terms && !this.free_tier_terms_accepted) return false;
      return true;
    },
    step_3_valid() {
      // Free-tier signups skip Stripe Elements entirely — no card,
      // no SetupIntent, no Element to wait on. Subscribe button is
      // always ready once they reach step 3.
      if (this.is_free_tier) return true;
      if (!this.stripe_publishable_key) return false;
      if (!this.stripe_elements_ready) return false;
      return true;
    },
    currentStepValid() {
      if (this.current_step === 1) return this.step_1_valid;
      if (this.current_step === 2) return this.step_2_valid;
      return this.step_3_valid;
    },
    currentStepLabel() {
      const s = this.steps.find(x => x.n === this.current_step);
      return s ? s.label : '';
    },
    progressPct() {
      const total = this.steps.length;
      if (total <= 1) return 100;
      return Math.round(((this.current_step - 1) / (total - 1)) * 100);
    },

    submit_label() {
      if (this.is_free_tier) return 'Subscribe Free';
      if (this.is_trial_promo) {
        return `Start Free · ${this.coupon_details.duration_in_months} mo, then $${this.formatted_price}/mo`;
      }
      if (this.coupon_applied && this.coupon_details && this.discounted_price < this.effective_price) {
        return `Subscribe · $${this.formatted_discounted_price}/mo + tax`;
      }
      return `Subscribe · $${this.formatted_price}/mo + tax`;
    },
    submit_label_busy() {
      return 'Authorizing payment…';
    },
    can_submit() {
      if (this.submitting) return false;
      if (!this.step_1_valid) return false;
      if (!this.step_2_valid) return false;
      if (!this.step_3_valid) return false;
      return true;
    },
  },

  async created() {
    if (this.$store.getters['Authenticate/isLoggedIn']) {
      this.$store.dispatch('Authenticate/safety_logout');
      this.$store.dispatch('Banner/hide');
    }
  },

  async mounted() {
    // Load the ZIP -> population lookup so market_population can sum real
    // residents instead of falling back to the 7,500-per-ZIP estimate.
    this.$store.dispatch('Market_billing/load_zip_data');
    if (this.user_sign_up && this.user_sign_up.phone_number) {
      this.first_name = this.user_sign_up.first_name || '';
      this.last_name = this.user_sign_up.last_name || '';
      this.company_name = this.user_sign_up.company_name || '';
      this.phone_number = (this.user_sign_up.phone_number || '').replace(config.phone_extension, '');
      this.email = this.user_sign_up.email || '';
      this.company_address = this.user_sign_up.company_address || '';
    }
    const q = (this.$route && this.$route.query) || {};
    if (typeof q.zips === 'string' && q.zips.length > 0) {
      this.claimed_zips = q.zips.split(',').map(z => z.trim()).filter(Boolean);
    }
    if (typeof q.pricing_mode === 'string' && ['calculated', 'free', 'custom'].includes(q.pricing_mode)) {
      this.claimed_pricing_mode = q.pricing_mode;
    }
    // Review-mode deep-link: ?step=2 / ?step=3 lands directly on that
    // step. Bypasses validation gates so QA can inspect billing without
    // filling the form. Long-term TODO: convert these into real routes
    // (/sign_up, /sign_up/agreement, /sign_up/billing) instead of a
    // single component with internal state.
    const stepFromQuery = parseInt(q.step, 10);
    if (stepFromQuery >= 1 && stepFromQuery <= this.steps.length) {
      this.current_step = stepFromQuery;
      if (stepFromQuery > this.max_step_reached) this.max_step_reached = stepFromQuery;
      // When QA jumps past step 1/2 via ?step=, prefill demo data on
      // those skipped steps so the Subscribe button is actually
      // clickable. Without this you can land on step 3 but the Subscribe
      // button stays disabled because step_1_valid / step_2_valid both
      // gate it. Each value is generated unique-per-load so successive
      // tests don't collide on phone_number / email.
      if (stepFromQuery >= 2) {
        const stamp = Date.now().toString().slice(-6);
        if (!this.first_name) this.first_name = 'QA';
        if (!this.last_name) this.last_name = `Tester${stamp}`;
        if (!this.company_name) this.company_name = `QA Brokerage ${stamp}`;
        // Raw 10 digits — the v-mask directive will format the display,
        // but vee-validate's `size:10` rule reads the underlying v-model
        // value. If we prefilled "202 555 1234" the rule would see 12
        // chars and silently block onSubscribeClick.
        if (!this.phone_number) this.phone_number = `202555${stamp.slice(-4)}`;
        if (!this.email) this.email = `qa-test-${stamp}@kiey-test.com`;
        if (!this.company_address) {
          this.company_address = '123 W Madison St, Chicago, IL 60602, USA';
          this.company_location = {
            address: '123 W Madison St, Chicago, IL 60602, USA',
            lat: 41.8819,
            lng: -87.6324,
            city: 'Chicago',
            state: 'IL',
          };
        }
        this.sms_consent = true;
        this.email_consent = true;
      }
      if (stepFromQuery >= 3) {
        this.terms_scrolled = true;
        this.billing_terms_accepted = true;
        // Also pre-accept the free-tier partner agreement so Subscribe
        // Free fires when QA applies a FREE coupon directly on step 3
        // (the Free Tier panel itself only renders on step 2; without
        // this flag, step_2_valid blocks the Subscribe button).
        this.free_tier_terms_accepted = true;
      }
      // The watcher that lazy-inits Stripe only fires on a TRANSITION
      // to step 3, not when starting there. Trigger directly.
      // Skip Stripe init when free tier (no card needed for $0 signup).
      if (stepFromQuery === 3 && !this.stripe_initialized && !this.is_free_tier) {
        this.stripe_initialized = true;
        this.initStripe();
      }
    }

    // Defense for stray entry points (direct URL paste, stale bookmark,
    // back-button after an aborted signup). The normal flow funnels
    // users through /my-market before they can land here, so this only
    // catches the edge case — silently send them back to draw a market.
    //
    // DISABLED for review mode so you can land on /sign_up directly
    // without zips in the URL. Re-enable before shipping.
    // if (!this.zip_count) {
    //   this.$router.replace('/my-market');
    //   return;
    // }

    this.initAddressAutocomplete();
    this.checkExistingOwner();
    this.loadFullTos();

    if (this.$store.state.Market_billing) {
      this.$store.dispatch('Market_billing/fetch_markets').catch(() => {});
      this.$store.dispatch('Market_billing/fetch_billing_info').catch(() => {});
    }
  },

  watch: {
    company_address(newVal) {
      if (this.company_location && this.company_location.address !== newVal) {
        this.company_location = null;
      }
      // Clear any sticky validator error on the address field so the
      // red "Please pick your address..." inline error disappears as
      // soon as the user edits the field again.
      if (this.$refs.company_address && this.$refs.company_address.setErrors) {
        try { this.$refs.company_address.setErrors([]); } catch (e) { /* noop */ }
      }
    },
    // Lazy-init Stripe when the user lands on step 3 — its mount target
    // only exists once that step's pane is shown. Skip when free tier
    // (no card needed).
    current_step(newStep) {
      if (newStep === 3 && !this.stripe_initialized && !this.is_free_tier) {
        this.stripe_initialized = true;
        this.initStripe();
      }
      if (newStep === 2) this.recheckTermsScrolled();
    },
  },

  methods: {
    isFreeTierCoupon(code) {
      return typeof code === 'string' && code.toUpperCase().includes('FREE');
    },

    // ---- Stepper navigation ----------------------------------------
    isStepCompleted(n) {
      // A step is only ✓ when its own validity is satisfied AND the user
      // has actually moved past it. Just clicking the next bullet must
      // not auto-mark the prior step complete.
      if (n >= this.current_step) return false;
      if (n === 1) return this.step_1_valid;
      if (n === 2) return this.step_2_valid;
      return false;
    },
    stepperItemClasses(n) {
      return {
        'is-active': this.current_step === n,
        'is-completed': this.isStepCompleted(n),
        'is-locked': !this.canJumpTo(n),
      };
    },
    canJumpTo(n) {
      // Forward navigation only to steps the user has actually validated
      // their way into. Stays open backwards so they can revise earlier
      // entries (coupon, name, etc.) without losing state.
      return n <= this.max_step_reached;
    },
    jumpTo(n) {
      // Don't bump max_step_reached on click — that's the goNext path's
      // job, gated by the per-step validators. Click here only navigates
      // to steps already reached (canJumpTo enforces).
      if (n <= this.max_step_reached) this.current_step = n;
    },
    async goNext() {
      this.error_message = '';
      if (this.current_step === 1) {
        // Run vee-validate across every required field on step 1
        // (First/Last name, Brokerage name, Cell, Email, Brokerage
        // Address). Surfaces per-field red text under any missing
        // input.
        const valid = await this.$refs.validator.validate();
        if (!valid) {
          this.error_message = 'Please complete every field before continuing.';
          return;
        }
        // Brokerage address must come from a Google Places dropdown
        // pick so we have structured lat/lng/city/state for Stripe Tax
        // + downstream territory matching. Free-text addresses are
        // blocked here.
        if (!this.company_location) {
          if (this.$refs.company_address && this.$refs.company_address.setErrors) {
            this.$refs.company_address.setErrors(['Please pick your address from the dropdown suggestions.']);
          }
          this.error_message = 'Please pick your address from the dropdown suggestions.';
          return;
        }
        // Both consent boxes are required — TCPA (SMS) and CAN-SPAM
        // (email) need explicit opt-in. step_1_valid also covers the
        // text fields but the validator above already handles those;
        // here we surface a clear message for the consent gates.
        if (!this.sms_consent || !this.email_consent) {
          this.error_message = 'Please accept both consent boxes to continue.';
          return;
        }
        this.advanceTo(2);
        return;
      }
      if (this.current_step === 2) {
        // Billing Terms scroll-to-bottom + acceptance is required; plus
        // the Free Tier Partner Agreement when the applied coupon is
        // a permanent free-tier code.
        if (!this.terms_scrolled) {
          this.error_message = 'Please scroll through the Billing Terms before continuing.';
          return;
        }
        if (!this.billing_terms_accepted) {
          this.error_message = 'Please accept the Billing Terms and Terms of Service to continue.';
          return;
        }
        if (this.show_free_tier_terms && !this.free_tier_terms_accepted) {
          this.error_message = 'Please accept the Free Tier Partner Agreement to continue.';
          return;
        }
        this.advanceTo(3);
        return;
      }
    },
    advanceTo(n) {
      this.current_step = n;
      if (n > this.max_step_reached) this.max_step_reached = n;
      this.scrollCardToTop();
    },
    goBack() {
      this.error_message = '';
      if (this.current_step <= 1) return;
      this.current_step -= 1;
      this.scrollCardToTop();
    },
    scrollCardToTop() {
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector
          ? this.$el.querySelector('.signup-card')
          : null;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    // ---- Terms scroll gate -----------------------------------------
    onTermsScroll(e) {
      if (this.terms_scrolled) return;
      const el = e && e.target;
      if (!el) return;
      const reached =
        el.scrollHeight - el.clientHeight <= 4 ||
        el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
      if (reached) this.terms_scrolled = true;
    },

    recheckTermsScrolled() {
      // Re-check whether the scrollbox content already fits without
      // scrolling (rare — the full ToS is long, but be safe). We must
      // only auto-pass the gate when:
      //   1. The full ToS markdown has actually loaded into the DOM,
      //   2. The scrollbox element is rendered with non-zero height,
      //   3. AND its full content fits within its viewport.
      // Skipping any of these checks would falsely set terms_scrolled
      // to true on step transitions (when the scrollbox is briefly
      // 0px tall) — which is exactly the bug the user just hit.
      this.$nextTick(() => {
        const el = this.$refs && this.$refs.terms_scrollbox;
        if (!el) return;
        if (!this.full_tos_loaded) return;
        if (el.clientHeight <= 0) return;
        if (el.scrollHeight <= 0) return;
        if (el.scrollHeight - el.clientHeight <= 4) this.terms_scrolled = true;
      });
    },

    loadFullTos() {
      if (this.full_tos_loaded || this.full_tos_loading) return;
      this.full_tos_loading = true;
      this.full_tos_error = '';
      fetch('/legal/terms_preview.md', { cache: 'no-store' })
        .then(res => { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
        .then(text => {
          this.full_tos_html = renderLegalMarkdown(text);
          this.full_tos_loaded = true;
          this.recheckTermsScrolled();
        })
        .catch(err => {
          console.error('[signup] failed to load full ToS:', err);
          this.full_tos_error = 'Could not load the full Terms of Service. Please refresh the page.';
        })
        .finally(() => { this.full_tos_loading = false; });
    },

    loadFreeTierTerms() {
      if (this.free_tier_terms_loaded || this.free_tier_terms_loading) return;
      this.free_tier_terms_loading = true;
      this.free_tier_terms_error = '';
      fetch('/legal/free_tier_partner_agreement.md', { cache: 'no-store' })
        .then(res => { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
        .then(text => {
          this.free_tier_terms_html = renderLegalMarkdown(text);
          this.free_tier_terms_loaded = true;
        })
        .catch(err => {
          console.error('[signup] failed to load free-tier agreement:', err);
          this.free_tier_terms_error = 'Could not load the Free Tier Partner Agreement.';
        })
        .finally(() => { this.free_tier_terms_loading = false; });
    },

    async applyCoupon() {
      const code = (this.coupon_input || '').trim();
      if (!code) return;
      this.coupon_error = '';
      this.coupon_validating = true;
      try {
        const axios = (await import('axios')).default;
        const response = await axios.post(
          config.api_url + 'coupons/validate',
          { coupon_code: code },
        );
        if (response.data && response.data.code === 'SUCCESS' && response.data.data) {
          this.coupon_applied_code = code;
          this.coupon_details = response.data.data;
          if (this.isFreeTierCoupon(code)) this.loadFreeTierTerms();
        } else {
          this.coupon_error = (response.data && response.data.message)
            || 'Invalid coupon code.';
        }
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.message)
          || 'Could not validate coupon. Please try again.';
        this.coupon_error = msg;
      } finally {
        this.coupon_validating = false;
      }
    },

    clearCoupon() {
      this.coupon_input = '';
      this.coupon_applied_code = '';
      this.coupon_details = null;
      this.coupon_error = '';
      this.free_tier_terms_accepted = false;
    },

    // ---- Stripe ----------------------------------------------------
    async initStripe() {
      if (!this.stripe_publishable_key) return;
      try {
        const intent = await this.$store.dispatch('Owner_sign_up/fetchSignupSetupIntent');
        this.stripe_setup_intent_id = intent.setup_intent_id;
        this.stripe_setup_intent_client_secret = intent.client_secret;

        this.stripe = await loadStripe(this.stripe_publishable_key);
        if (!this.stripe) throw new Error('Stripe.js failed to load.');
        this.stripe_elements = this.stripe.elements({
          clientSecret: this.stripe_setup_intent_client_secret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#29abe2',
              colorBackground: '#ffffff',
              colorText: '#061d47',
              fontFamily: 'Arial, sans-serif',
              borderRadius: '8px',
            },
          },
        });

        this.stripe_payment_element = this.stripe_elements.create('payment', {
          // Collect the FULL card billing address on the Payment Element
          // — separate from the brokerage Company Address captured on
          // Step 1. Brokerage address still becomes Customer.address (for
          // invoices + Stripe Tax); this is the cardholder's billing
          // address attached to the PaymentMethod for AVS / dispute
          // protection. The two are deliberately independent.
          fields: {
            billingDetails: {
              address: {
                country: 'auto',
                line1: 'auto',
                line2: 'auto',
                city: 'auto',
                state: 'auto',
                postalCode: 'auto',
              },
            },
          },
          defaultValues: {
            billingDetails: { address: { country: 'US' } },
          },
          // Suppress wallet promos that would otherwise float in next
          // to the card field. The SetupIntent itself is restricted to
          // payment_method_types: ['card'] which keeps the Bank / Cash
          // App / Bancontact tabs out, but Link + Apple Pay + Google
          // Pay still try to render their own UI affordances unless
          // explicitly suppressed here.
          wallets: {
            link: 'never',
            applePay: 'never',
            googlePay: 'never',
          },
        });

        await this.$nextTick();
        const slot = this.$refs.stripe_payment_element;
        if (!slot) throw new Error('Payment slot missing.');
        this.stripe_payment_element.mount(slot);
        this.stripe_payment_element.on('ready', () => {
          this.stripe_elements_ready = true;
        });
        this.stripe_payment_element.on('loaderror', (e) => {
          this.stripe_load_error = (e && e.error && e.error.message)
            || 'Failed to load the secure payment form.';
        });
      } catch (err) {
        console.error('[signup] Stripe init failed:', err);
        this.stripe_load_error = (err && err.message) ||
          'Failed to load the secure payment form. Please refresh and try again.';
        this.stripe_initialized = false;
      }
    },

    async onSubscribeClick() {
      console.log('[signup] Subscribe clicked', {
        can_submit: this.can_submit,
        submitting: this.submitting,
        is_free_tier: this.is_free_tier,
        is_trial_promo: this.is_trial_promo,
        coupon_code: this.coupon_applied_code,
      });
      if (!this.can_submit) return;
      if (this.submitting) return;
      this.error_message = '';
      this.submitting = true;
      try {
        const valid = await this.$refs.validator.validate();
        console.log('[signup] validator result:', valid);
        if (!valid) {
          // Surface the failure so the user sees something happened.
          // Without this message a failed validator silently returns and
          // the Subscribe click appears to do nothing — exactly the
          // "nothing happens" bug.
          this.error_message = 'Please double-check the form fields and try again.';
          this.submitting = false;
          return;
        }
        // Address must come from a Google Places pick so we have
        // structured lat/lng/city/state for Stripe Tax accuracy + future
        // matching. Free-text addresses are blocked. Defense in depth
        // for the case where the user reached step 3 without going
        // through goNext() (e.g. via ?step=3 prefill or back-button).
        if (!this.company_location) {
          if (this.$refs.company_address && this.$refs.company_address.setErrors) {
            this.$refs.company_address.setErrors(['Please pick your address from the dropdown suggestions.']);
          }
          this.error_message = 'Please pick your address from the dropdown suggestions.';
          this.current_step = 1;
          this.submitting = false;
          return;
        }

        // Stripe Elements card authorization — only for paid signups.
        // Free-tier signups (100% off coupon) skip the entire SetupIntent
        // confirm step: no card is collected, the subscription is created
        // server-side with the coupon and no default_payment_method.
        if (!this.is_free_tier) {
          const confirmResult = await this.stripe.confirmSetup({
            elements: this.stripe_elements,
            confirmParams: {
              return_url: window.location.origin + '/sign_up/welcome',
            },
            redirect: 'if_required',
          });
          if (confirmResult.error) {
            console.error('[signup] confirmSetup error:', confirmResult.error);
            this.error_message = (confirmResult.error.message)
              || 'Could not authorize your card. Please check the details and try again.';
            this.submitting = false;
            return;
          }
          const seti = confirmResult.setupIntent;
          if (!seti || seti.status !== 'succeeded') {
            this.error_message = 'Card authorization did not complete. Please try again.';
            this.submitting = false;
            return;
          }
        }

        const payload = {
          first_name: this.first_name,
          last_name: this.last_name,
          email: this.email,
          phone_number: this.phone_number,
          company_name: this.company_name,
          address: this.company_address,
          location: this.company_location,
          // The scroll-gated billing-terms acceptance is a single
          // clickwrap covering both ToS and Billing Terms (rendered
          // side-by-side in the same scrollbox), so we satisfy the
          // existing tos_accepted gate from the same checkbox state.
          tos_accepted: this.billing_terms_accepted,
          tos_version: config.tos_current_version,
          billing_terms_accepted: this.billing_terms_accepted,
          billing_terms_version: this.billing_terms_version,
          // Free-tier path has no SetupIntent — server skips card attach.
          setup_intent_id: this.is_free_tier ? null : this.stripe_setup_intent_id,
          selected_zips: this.claimed_zips,
          pricing_mode: this.claimed_pricing_mode,
        };
        if (this.coupon_applied_code) {
          payload.coupon_code = this.coupon_applied_code;
        }
        if (this.show_free_tier_terms && this.free_tier_terms_accepted) {
          payload.free_terms_accepted = true;
          payload.free_terms_version = this.free_terms_version;
        }

        let response;
        try {
          response = await this.$store.dispatch('Owner_sign_up/signupWithSubscription', payload);
        } catch (storeErr) {
          this.error_message = (storeErr && storeErr.message) || 'Subscription failed.';
          this.submitting = false;
          return;
        }

        const data = (response && response.data) || {};
        if (data.requires_action && data.payment_intent_client_secret) {
          const result = await this.stripe.confirmCardPayment(data.payment_intent_client_secret);
          if (result.error) {
            this.error_message = (result.error.message)
              || 'Additional authentication failed. Your subscription was created but is not active — please contact support.';
            this.submitting = false;
            return;
          }
        }

        this.$router.push('/sign_up/welcome');
      } catch (err) {
        console.error('[signup] subscribe failed:', err);
        this.error_message = (err && err.message) || 'Subscription failed. Please try again.';
        this.submitting = false;
      }
    },

    checkExistingOwner() {
      try {
        const u = this.authenticatedUser;
        if (!u || !Array.isArray(u.tenants_list)) {
          this.existingOwnerCheckDone = true;
          return;
        }
        const existing = u.tenants_list.find(t =>
          t && t.role === 'owner' && t.status !== 'inactive' && t.status !== 'deleted'
        );
        if (existing) this.existingOwnerTenant = existing;
      } catch (e) {
        console.warn('[signup] existing-owner check failed', e && e.message);
      } finally {
        this.existingOwnerCheckDone = true;
      }
    },

    goToDashboard() {
      this.$router.push('/owner/owner-admin-panel');
    },

    dismissExistingOwner() {
      this.existingOwnerTenant = null;
    },

    async initAddressAutocomplete() {
      await this.$gmapApiPromiseLazy();
      await this.$nextTick();
      const input = this.$refs.addressField && this.$refs.addressField.$el
        && this.$refs.addressField.$el.querySelector('input');
      if (!input) return;
      input.setAttribute('placeholder', '');
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: ['us', 'ca'] },
      });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.formatted_address) {
          this.company_address = place.formatted_address;
          this.company_location = extractLocation(place);
        }
      });
    },
  },

  beforeDestroy() {
    if (this.stripe_payment_element) {
      try { this.stripe_payment_element.unmount(); } catch (e) { /* noop */ }
    }
  },
};
</script>

<style scoped>
.signup-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 16px 80px;
  box-sizing: border-box;
  background-color: #f5f7fb;
}
.signup-card {
  width: 100%;
  max-width: 760px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 32px 36px 28px;
  box-shadow: 0 8px 24px rgba(6, 29, 71, 0.08);
  box-sizing: border-box;
}

/* ---------- Header with persistent price strip ---------- */
.signup-header { margin-bottom: 24px; }
.signup-header__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}
.signup-header__title-block { flex: 1 1 auto; min-width: 0; }
.signup-title {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 4px;
}
.signup-subtitle {
  color: #4a4a4a;
  font-size: 13.5px;
  margin: 0;
}
.signup-link {
  color: #29abe2;
  text-decoration: none;
  font-weight: 600;
}
.signup-link:hover { text-decoration: underline; }

.signup-header__price-block { flex: 0 0 auto; }
.price-strip {
  background: linear-gradient(135deg, #061d47 0%, #0a2a64 100%);
  color: #ffffff;
  padding: 10px 16px;
  border-radius: 10px;
  text-align: right;
  min-width: 160px;
}
.price-strip__amount {
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}
.price-strip__per {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.85;
  margin-left: 2px;
}
.price-strip__note {
  font-size: 11px;
  margin-top: 4px;
  opacity: 0.85;
  letter-spacing: 0.2px;
}

/* ---------- Stepper indicator ---------- */
.stepper { margin: 0 0 26px; }
.stepper__list {
  display: flex;
  align-items: center;
  justify-content: space-between;
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
}
.stepper__item {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
}
.stepper__btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  font-family: inherit;
  flex: 0 0 auto;
}
.stepper__btn:disabled { cursor: not-allowed; }
.stepper__btn:focus-visible { outline: 2px solid #29abe2; outline-offset: 2px; border-radius: 6px; }
.stepper__bullet {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e4e8f1;
  color: #6a7388;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.stepper__check { font-size: 14px; line-height: 1; }
.is-active .stepper__bullet {
  background: #29abe2;
  color: #ffffff;
  box-shadow: 0 0 0 4px rgba(41, 171, 226, 0.18);
}
.is-completed .stepper__bullet {
  background: #2dbf6c;
  color: #ffffff;
}
.is-locked .stepper__bullet {
  background: #eef0f5;
  color: #b0b6c4;
}
.stepper__label {
  color: #6a7388;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.is-active .stepper__label { color: #061d47; }
.is-completed .stepper__label { color: #2dbf6c; }
.is-locked .stepper__label { color: #b0b6c4; }
.stepper__connector {
  flex: 1 1 auto;
  height: 2px;
  background: #e4e8f1;
  margin: 0 12px;
  border-radius: 2px;
}
.stepper__bar { display: none; }
.stepper__mobile { display: none; }
@media (max-width: 560px) {
  .stepper__list { display: none; }
  .stepper__bar {
    display: block;
    height: 4px;
    background: #e4e8f1;
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 8px;
  }
  .stepper__bar-fill {
    height: 100%;
    background: #29abe2;
    transition: width 0.25s ease;
  }
  .stepper__mobile {
    display: block;
    color: #6a7388;
    font-size: 12.5px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
}

/* ---------- Step pane ---------- */
.step-pane { animation: stepFade 0.18s ease-out; }
@keyframes stepFade {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.step-title {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
  letter-spacing: 0.1px;
}
.step-caption {
  color: #6a7388;
  font-size: 13.5px;
  line-height: 1.5;
  margin: 0 0 20px;
}
.step-subheading {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin: 26px 0 6px;
}
.step-subheading-spaced { margin-top: 32px; }
.section-caption {
  color: #6a7388;
  font-size: 13.5px;
  line-height: 1.5;
  margin: 0 0 14px;
}

/* ---------- Step nav ---------- */
.step-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid #eef0f5;
}
.step-nav__back {
  background: transparent;
  border: none;
  color: #6a7388;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 4px;
}
.step-nav__back:disabled { opacity: 0.5; cursor: not-allowed; }
.step-nav__back:hover:not(:disabled) { color: #061d47; }
.step-nav__forward { display: flex; }
.step-nav__continue {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding: 0 28px;
  border-radius: 23px;
  background: #061d47;
  color: #ffffff;
  font-family: Arial, sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  border: 1px solid transparent;
  cursor: pointer;
  min-width: 180px;
  letter-spacing: 0.4px;
}
.step-nav__continue:hover:not(:disabled) { background: #0a2a64; }
.step-nav__continue:disabled { opacity: 0.45; cursor: not-allowed; }
.step-nav__subscribe {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 32px;
  border-radius: 25px;
  background: #29abe2;
  color: #ffffff;
  font-family: Arial, sans-serif;
  font-size: 15px;
  font-weight: 700;
  border: 1px solid transparent;
  cursor: pointer;
  min-width: 280px;
  letter-spacing: 0.4px;
}
.step-nav__subscribe:hover:not(:disabled) { background: #1f9bd0; }
.step-nav__subscribe:disabled { opacity: 0.5; cursor: not-allowed; }

.subscribe-error {
  margin: 16px 0 0;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f4c1c1;
  color: #c0392b;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}
.subscribe-footer {
  margin: 16px 0 0;
  text-align: center;
  color: #6a7388;
  font-size: 12px;
  line-height: 1.5;
}

/* ---------- Account form ---------- */
.account-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
}
.account-grid__cell { min-width: 0; }
@media (max-width: 720px) {
  .account-grid { grid-template-columns: 1fr; }
}

/* Existing-owner banner */
.existing-owner-banner {
  margin: 0 0 18px 0;
  padding: 16px 18px;
  background: #f4f7ff;
  border: 1px solid #c7d3f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  color: #061d47;
  font-size: 14px;
  line-height: 1.45;
}
.existing-owner-banner__body { flex: 1 1 320px; }
.existing-owner-banner__body strong { display: block; margin-bottom: 4px; color: #061d47; }
.existing-owner-banner__actions { display: flex; gap: 8px; flex-shrink: 0; }
.existing-owner-banner__btn-primary,
.existing-owner-banner__btn-ghost {
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.existing-owner-banner__btn-primary { background: #061d47; color: #fff; border-color: #061d47; }
.existing-owner-banner__btn-primary:hover { background: #0a2a64; border-color: #0a2a64; }
.existing-owner-banner__btn-ghost { background: #fff; color: #061d47; border-color: #c7d3f0; }
.existing-owner-banner__btn-ghost:hover { background: #f0f4fc; }

/* Consent rows */
.signup-consent-tight >>> .consent-checkboxes { margin-top: 4px !important; }
.signup-consent-tight >>> .v-input--checkbox { margin-top: 0 !important; padding-top: 0 !important; }
.signup-consent-tight >>> .v-input__slot { margin-bottom: 0 !important; }
.signup-consent-tight >>> .v-messages { min-height: 0 !important; }

/* Pricing card */
.pricing-card {
  border: 1px solid #e4e8f1;
  border-radius: 10px;
  padding: 18px 20px;
  background: #ffffff;
}
.pricing-card-recap {
  background: #f4f7ff;
  border-color: #c7d3f0;
}
.pricing-recap-note {
  margin: 8px 0 0;
  color: #5a6478;
  font-size: 12.5px;
  line-height: 1.5;
}
.pricing-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  line-height: 1.45;
}
.pricing-label { color: #2d2d2d; flex: 1 1 240px; }
.pricing-value { color: #061d47; font-weight: 600; white-space: nowrap; }
.pricing-row-emphasis { font-size: 16px; }
.pricing-row-emphasis .pricing-value { color: #061d47; font-size: 18px; }
.pricing-row-tax .pricing-tax-note { color: #6a7388; font-weight: 500; font-size: 13px; }
.pricing-divider { height: 1px; background: #e4e8f1; margin: 8px 0 4px; }
.pricing-row-total { font-weight: 700; }
.pricing-row-total .pricing-label { color: #061d47; font-weight: 700; }
.pricing-coupon-note {
  margin: 6px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f4f7ff;
  color: #061d47;
  font-size: 13px;
}
.pricing-coupon-note code {
  font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
  background: #ffffff;
  border-radius: 4px;
  padding: 1px 5px;
}
.pricing-strikethrough {
  text-decoration: line-through;
  color: #9aa2b4;
  font-weight: 500;
  margin-right: 8px;
}
.pricing-free-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  background: #2dbf6c;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  vertical-align: middle;
}
.pricing-trial-after {
  display: inline-block;
  margin-left: 10px;
  color: #6a7388;
  font-weight: 500;
  font-size: 13px;
  font-style: italic;
  vertical-align: middle;
}
/* Header price strip — green-gradient variant for free tier */
.price-strip--free {
  background: linear-gradient(135deg, #1b8b4f 0%, #2dbf6c 100%);
}

.muted { color: #9aa2b4; }

/* Coupon */
.coupon-section { margin-top: 22px; }
.coupon-section--step3 { margin-top: 18px; padding-top: 4px; }
.coupon-summary-step3 {
  background: #f5f8fc;
  border: 1px solid #e3eaf5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-family: Roboto, Arial, sans-serif;
  font-size: 13px;
}
.coupon-summary-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.coupon-summary-label {
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.04em;
}
.coupon-summary-value { color: #041332; flex: 1; min-width: 0; }
.coupon-summary-detail { color: #6b7280; font-weight: 400; }
.coupon-summary-change {
  background: none;
  border: none;
  color: #2bace2;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
}
.coupon-summary-change:hover { text-decoration: underline; }
.coupon-toggle {
  background: transparent;
  border: none;
  padding: 6px 0;
  color: #29abe2;
  font-size: 14px;
  font-weight: 600;
  font-family: Arial, sans-serif;
  cursor: pointer;
}
.coupon-toggle:hover { text-decoration: underline; }
.coupon-toggle:focus-visible { outline: 2px solid #29abe2; outline-offset: 2px; border-radius: 4px; }
.coupon-panel { padding: 0; margin-top: 4px; }
.coupon-label { display: block; color: #6a7388; font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.coupon-row { display: flex; flex-wrap: wrap; gap: 10px; }
.coupon-input {
  flex: 1 1 200px;
  min-width: 160px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #c7d3f0;
  border-radius: 8px;
  background: #fff;
  color: #061d47;
  font-size: 14px;
  font-family: Arial, sans-serif;
  box-sizing: border-box;
}
.coupon-input:disabled { background: #eef0f5; color: #6a7388; }
.coupon-input:focus { outline: none; border-color: #29abe2; box-shadow: 0 0 0 2px rgba(41, 171, 226, 0.2); }
.coupon-btn {
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  border: 1px solid #061d47;
  background: #061d47;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: Arial, sans-serif;
  cursor: pointer;
}
.coupon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.coupon-btn:hover:not(:disabled) { background: #0a2a66; }
.coupon-btn-clear { background: #fff; color: #061d47; }
.coupon-btn-clear:hover { background: #061d47; color: #fff; }
.coupon-note { color: #5a6478; font-size: 12px; margin: 8px 0 0; }
.coupon-note-ok { color: #19a99d; font-weight: 600; }

.free-tier-block { margin-top: 22px; }

/* Terms scrollbox */
.terms-scrollbox {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #c7d3f0;
  border-radius: 10px;
  padding: 18px 20px;
  background: #fafbfd;
  font-size: 13px;
  line-height: 1.55;
  color: #2d2d2d;
}
.terms-scrollbox-full { max-height: none; overflow-y: visible; }
.terms-scrollbox:focus-visible { outline: 2px solid #29abe2; outline-offset: 2px; }
.terms-end-marker {
  text-align: center;
  margin: 18px 0 4px;
  color: #8e96a8;
  font-size: 11px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 600;
}
.terms-scroll-hint { margin: 18px 4px 14px 4px; color: #6a7388; font-size: 12.5px; font-style: italic; }
.terms-instruction {
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #f1d77a;
  color: #5a4500;
  font-size: 13px;
  line-height: 1.5;
}
.terms-instruction strong { color: #443100; }
.terms-instruction-done {
  background: #eaf7ee;
  border-color: #b8e2c4;
  color: #1f6b34;
}
.terms-instruction-done strong { color: #14532d; }
.terms-consent-disabled { opacity: 0.55; cursor: not-allowed; }
.terms-consent-disabled input[type="checkbox"] { cursor: not-allowed; }
.terms-divider { border: none; border-top: 1px solid #d6deef; margin: 22px 0 18px; }
.terms-fulltos { font-size: 12.5px; line-height: 1.55; color: #2d2d2d; }
.terms-fulltos h1, .terms-fulltos h2, .terms-fulltos h3 { color: #061d47; margin-top: 18px; margin-bottom: 8px; }
.terms-fulltos h1 { font-size: 15px; font-weight: 700; }
.terms-fulltos h2 { font-size: 13.5px; font-weight: 700; }
.terms-fulltos h3 { font-size: 12.5px; font-weight: 700; }
.terms-fulltos p { margin: 8px 0; }
.terms-fulltos ul, .terms-fulltos ol { margin: 6px 0 10px 20px; padding: 0; }
.terms-fulltos li { margin: 3px 0; }
.terms-fulltos strong { color: #061d47; }
.terms-fulltos a { color: #29abe2; }
.terms-heading {
  color: #061d47;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 12px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.terms-version { color: #6a7388; font-size: 11px; font-weight: 500; font-family: monospace; }
.terms-list { margin: 0 0 12px; padding: 0 0 0 18px; }
.terms-list li { margin-bottom: 10px; }
.terms-list strong { color: #061d47; }
.terms-footer { margin: 12px 0 0; padding-top: 10px; border-top: 1px solid #e4e8f1; color: #5a6478; font-size: 12px; }

.free-terms-body { color: #2d2d2d; line-height: 1.6; font-size: 13px; }
.free-terms-body >>> h2 { color: #061d47; font-size: 15px; font-weight: 700; margin: 18px 0 8px; }
.free-terms-body >>> h2:first-child { margin-top: 0; }
.free-terms-body >>> h3 { color: #061d47; font-size: 13px; font-weight: 700; margin: 14px 0 6px; }
.free-terms-body >>> p { margin: 0 0 10px; }
.free-terms-body >>> ul, .free-terms-body >>> ol { margin: 6px 0 12px; padding-left: 20px; }
.free-terms-body >>> li { margin-bottom: 4px; }
.free-terms-body >>> strong { color: #061d47; font-weight: 700; }
.free-terms-body >>> a { color: #29abe2; text-decoration: none; }
.free-terms-body >>> hr { border: 0; border-top: 1px solid #e4e8f1; margin: 18px 0; }

.terms-consent {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 18px;
  padding: 16px 18px;
  border: 1px solid #c7d3f0;
  border-radius: 8px;
  background: #f4f7ff;
  font-size: 14px;
  line-height: 1.5;
  color: #061d47;
  cursor: pointer;
}
.terms-consent input[type='checkbox'] {
  margin-top: 2px;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Stripe Payment Element host */
.stripe-element-mount {
  border: 1px solid #c7d3f0;
  border-radius: 10px;
  padding: 14px 16px;
  background: #ffffff;
  min-height: 56px;
}
.payment-loading { font-size: 13px; padding: 8px 4px 0; }
.payment-warning {
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f4c1c1;
  color: #c0392b;
  font-size: 13px;
  line-height: 1.5;
}
.payment-warning code {
  background: #ffffff;
  border-radius: 4px;
  padding: 1px 5px;
  font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
}

/* ---------- Tablet / small laptop ---------- */
@media (max-width: 720px) {
  .signup-card { padding: 22px 18px 22px; }
  .signup-title { font-size: 20px; }
  .price-strip__amount { font-size: 20px; }
  /* Keep label-left / value-right layout — just shrink the basis so
     the label doesn't reserve 240px (which on this row turned into
     240px of *height* once we briefly flipped to flex-direction:
     column — leaving giant blank gaps between Market / Population /
     Your monthly fee). */
  .pricing-row { gap: 12px; align-items: baseline; }
  .pricing-row .pricing-label { flex: 1 1 auto; min-width: 0; }
  .pricing-row .pricing-value { white-space: normal; flex: 0 0 auto; text-align: right; }
  .step-nav__continue,
  .step-nav__subscribe { width: 100%; min-width: 0; flex: 1 1 auto; }
  .step-nav__forward { flex: 1 1 auto; }
  .step-subheading-spaced { margin-top: 26px; }
  .signup-header__row { flex-direction: column; align-items: stretch; gap: 12px; }
  .signup-header__price-block { width: 100%; }
  .price-strip {
    width: 100%;
    box-sizing: border-box;
    min-width: 0;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    text-align: left;
    padding: 10px 14px;
  }
  .price-strip__note { margin-top: 0; text-align: right; }
}

/* ---------- Phones (≤ 480px) ---------- */
@media (max-width: 480px) {
  .signup-page { padding: 16px 10px 60px; }
  .signup-card { padding: 18px 14px 18px; border-radius: 10px; }
  .signup-title { font-size: 18px; line-height: 1.25; }
  .signup-subtitle { font-size: 12.5px; }

  .price-strip { padding: 10px 12px; }
  .price-strip__amount { font-size: 22px; }
  .price-strip__per { font-size: 12px; }
  .price-strip__note { font-size: 10.5px; }

  .step-title { font-size: 18px; line-height: 1.3; }
  .step-caption { font-size: 13px; margin-bottom: 16px; }
  .step-subheading { font-size: 12.5px; margin-top: 22px; }
  .section-caption { font-size: 13px; margin-bottom: 12px; }

  .market-card,
  .pricing-card { padding: 14px 14px; }
  .market-card-row { gap: 12px; }
  .market-card-cell { flex: 1 1 100%; }
  .pricing-card-recap { padding: 14px 14px; }
  .pricing-recap-note { font-size: 12px; }

  .coupon-row { flex-direction: column; gap: 8px; }
  .coupon-input,
  .coupon-btn { width: 100%; flex: 1 1 100%; min-width: 0; box-sizing: border-box; }

  .terms-scrollbox {
    padding: 14px 14px;
    max-height: 280px;
    font-size: 12.5px;
    line-height: 1.5;
  }
  .terms-instruction { font-size: 12.5px; padding: 9px 12px; line-height: 1.45; }
  .terms-consent { padding: 14px 14px; font-size: 13px; gap: 10px; }
  .terms-consent input[type='checkbox'] { width: 16px; height: 16px; }
  .terms-heading { font-size: 13px; }

  .stripe-element-mount { padding: 12px 12px; }
  .payment-warning { font-size: 12.5px; padding: 10px 12px; }

  .step-nav {
    gap: 8px;
    padding-top: 18px;
    margin-top: 22px;
    flex-wrap: nowrap;
  }
  .step-nav__back {
    padding: 10px 0;
    font-size: 13px;
    flex: 0 0 auto;
  }
  .step-nav__continue {
    height: 44px;
    font-size: 14px;
    padding: 0 18px;
    min-width: 0;
  }
  .step-nav__subscribe {
    height: 48px;
    font-size: 13.5px;
    padding: 0 16px;
    min-width: 0;
    letter-spacing: 0.2px;
  }
  .subscribe-error { font-size: 12.5px; padding: 10px 12px; }
  .subscribe-footer { font-size: 11.5px; }

  .existing-owner-banner {
    padding: 14px;
    gap: 10px;
    font-size: 13px;
  }
  .existing-owner-banner__body { flex: 1 1 100%; }
  .existing-owner-banner__actions { flex: 1 1 100%; }
  .existing-owner-banner__btn-primary,
  .existing-owner-banner__btn-ghost { flex: 1 1 50%; padding: 8px 12px; font-size: 12.5px; }

  .free-tier-block { margin-top: 16px; }
  .free-terms-body { font-size: 12.5px; }
}

/* ---------- Tiny phones (≤ 360px, e.g. iPhone SE 1st gen) ---------- */
@media (max-width: 360px) {
  .signup-card { padding: 16px 12px; }
  .signup-title { font-size: 17px; }
  .step-title { font-size: 17px; }
  .price-strip__amount { font-size: 20px; }
  .price-strip__note { font-size: 10px; }
  .step-nav__subscribe {
    font-size: 12.5px;
    padding: 0 12px;
    letter-spacing: 0;
  }
}
</style>

<style>
.signup-page .sing_up input { font-size: 13px !important; }
.signup-page .sing_up .v-label { font-size: 13px !important; }
</style>
